import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FunnelIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

interface OutboundCallback {
  id: number;
  transactionId: string;
  callbackUrl: string;
  payload: any;
  status: string;
  attempts: number;
  httpStatusCode: number;
  errorMessage: string;
  responseData: any;
  lastAttemptAt: string;
  createdAt: string;
}

const OutboundCallbacks: React.FC = () => {
  const navigate = useNavigate();
  const [filteredCallbacks, setFilteredCallbacks] = useState<OutboundCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCallback, setSelectedCallback] = useState<OutboundCallback | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [excludeLocalhost, setExcludeLocalhost] = useState(false);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0, pending: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchCallbacks();
    fetchStats();
    const interval = setInterval(() => {
      fetchCallbacks();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [statusFilter, excludeLocalhost]); // eslint-disable-line react-hooks/exhaustive-deps


  const fetchCallbacks = async (page = currentPage) => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const statusParam = statusFilter !== 'all' ? statusFilter : '';
      const response = await axios.get(
        `${API_BASE_URL}/callback/outbound?page=${page}&limit=${itemsPerPage}&status=${statusParam}&excludeLocalhost=${excludeLocalhost}`
      );
      setFilteredCallbacks(response.data.data);
      setCurrentPage(response.data.meta.page);
      setTotalPages(response.data.meta.totalPages);
      setTotalItems(response.data.meta.total);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      const response = await axios.get(`${API_BASE_URL}/callback/outbound/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRetry = async (id: number) => {
    setRetrying(id);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
      await axios.post(`${API_BASE_URL}/callback/outbound/${id}/retry`);
      await fetchCallbacks();
      await fetchStats();
      alert('Callback retried successfully!');
    } catch (error) {
      console.error('Error retrying callback:', error);
      alert('Failed to retry callback');
    } finally {
      setRetrying(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      success: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Callbacks</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor and retry callbacks sent to external endpoints
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => navigate('/callbacks')}
            className="inline-flex items-center px-4 py-2 border border-indigo-300 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            View Inbound Callbacks
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowPathIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.total}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Success</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.success}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.failed}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 rounded text-sm ${statusFilter === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            All ({stats.total})
          </button>
          <button
            onClick={() => setStatusFilter('success')}
            className={`px-3 py-1 rounded text-sm ${statusFilter === 'success' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Success ({stats.success})
          </button>
          <button
            onClick={() => setStatusFilter('failed')}
            className={`px-3 py-1 rounded text-sm ${statusFilter === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Failed ({stats.failed})
          </button>
          <button
            onClick={() => setStatusFilter('pending')}
            className={`px-3 py-1 rounded text-sm ${statusFilter === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            Pending ({stats.pending})
          </button>
          <div className="ml-auto flex items-center">
            <input
              type="checkbox"
              id="excludeLocalhost"
              checked={excludeLocalhost}
              onChange={(e) => setExcludeLocalhost(e.target.checked)}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="excludeLocalhost" className="ml-2 text-sm text-gray-700">
              Hide Localhost
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">HTTP Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Attempt</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCallbacks.map((callback) => (
                <tr key={callback.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{callback.transactionId}</td>
                  <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs" title={callback.callbackUrl}>
                    {callback.callbackUrl}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(callback.status)}`}>
                      {callback.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {callback.httpStatusCode || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{callback.attempts}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {callback.lastAttemptAt ? formatDate(callback.lastAttemptAt) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <button
                      onClick={() => setSelectedCallback(callback)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5 inline" /> View
                    </button>
                    {callback.status === 'failed' && (
                      <button
                        onClick={() => handleRetry(callback.id)}
                        disabled={retrying === callback.id}
                        className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                      >
                        {retrying === callback.id ? (
                          <span className="inline-flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-1"></div>
                            Retrying...
                          </span>
                        ) : (
                          <>
                            <ArrowPathIcon className="h-5 w-5 inline" /> Retry
                          </>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCallbacks.length === 0 && (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No callbacks</h3>
              <p className="mt-1 text-sm text-gray-500">
                {statusFilter === 'all' ? 'No callbacks have been sent yet.' : `No ${statusFilter} callbacks found.`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedCallback && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedCallback(null)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Callback Details #{selectedCallback.id}
                  </h3>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(selectedCallback.status)}`}>
                    {selectedCallback.status}
                  </span>
                </div>
                <div className="space-y-4">
                  <div>
                    <strong className="text-sm text-gray-700">Transaction ID:</strong>
                    <p className="text-sm text-gray-900 font-mono">{selectedCallback.transactionId}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Callback URL:</strong>
                    <p className="text-sm text-gray-900 break-all">{selectedCallback.callbackUrl}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Status:</strong>
                    <p className="text-sm text-gray-900">{selectedCallback.status}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">HTTP Status Code:</strong>
                    <p className={`text-sm font-bold ${selectedCallback.httpStatusCode >= 200 && selectedCallback.httpStatusCode < 300 ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedCallback.httpStatusCode || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Attempts:</strong>
                    <p className="text-sm text-gray-900">{selectedCallback.attempts}</p>
                  </div>
                  {selectedCallback.errorMessage && (
                    <div>
                      <strong className="text-sm text-red-700">Error Message:</strong>
                      <p className="text-sm text-red-900 bg-red-50 p-3 rounded mt-1">{selectedCallback.errorMessage}</p>
                    </div>
                  )}
                  <div>
                    <strong className="text-sm text-gray-700">Payload Sent:</strong>
                    <pre className="mt-2 text-xs bg-gray-100 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedCallback.payload, null, 2)}
                    </pre>
                  </div>
                  {selectedCallback.responseData && (
                    <div>
                      <strong className="text-sm text-gray-700">Response Data:</strong>
                      <pre className="mt-2 text-xs bg-gray-100 p-4 rounded-lg overflow-x-auto">
                        {JSON.stringify(selectedCallback.responseData, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div>
                    <strong className="text-sm text-gray-700">Last Attempt:</strong>
                    <p className="text-sm text-gray-900">{selectedCallback.lastAttemptAt ? formatDate(selectedCallback.lastAttemptAt) : '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-gray-700">Created:</strong>
                    <p className="text-sm text-gray-900">{formatDate(selectedCallback.createdAt)}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                {selectedCallback.status === 'failed' && (
                  <button
                    type="button"
                    onClick={() => {
                      handleRetry(selectedCallback.id);
                      setSelectedCallback(null);
                    }}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
                  >
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Retry Now
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => setSelectedCallback(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg mt-4">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => fetchCallbacks(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => fetchCallbacks(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => fetchCallbacks(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => fetchCallbacks(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => fetchCallbacks(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundCallbacks;
